"use client";

import Grid from "@mui/material/Grid2";
import { useFormikContext } from "formik";
import React, { useRef } from "react";
import { useNavigate } from "react-router-dom";

import MDBox from "@/components/atoms/MDBox";
import { MDButton } from "@/components/atoms/MDButton";
import { ProductFormData } from "@/components/screens/ProductFormPage/types";
import ROUTE_URLS from "@/constants/route-urls";
import useLanguage from "@/contexts/language-context";
import { ProductFormPageMessages } from "@/messages/products/product-form-page.messages";

import AdditionalInfo from "./AdditionalInfo";
import Categories from "./Categories";
import { useSyncedSecondRowHeight } from "./use-synced-second-row-height";
import ProductImage from "./ProductImage";
import ProductInfo from "./ProductInfo";
import VariationInfo from "./Variations";

const PRODUCT_FORM_SECOND_ROW_FALLBACK_HEIGHT_PX = 300;

type Props = {
  messages: ProductFormPageMessages;
  isLoading: boolean;
  onSubmit: () => Promise<void>;
  productId?: number | null;
  isEditing?: boolean;
};

function ProductForm({ messages, isLoading, onSubmit, productId, isEditing = false }: Props) {
  const { lang } = useLanguage();
  const router = useNavigate();
  const additionalInfoCardRef = useRef<HTMLDivElement>(null);
  const measuredSecondRowHeight = useSyncedSecondRowHeight(additionalInfoCardRef);
  const categoriesCardHeightPx =
    measuredSecondRowHeight > 0 ? measuredSecondRowHeight : PRODUCT_FORM_SECOND_ROW_FALLBACK_HEIGHT_PX;

  const handleSaveClick = async () => {
    console.log("[BUTTON_CLICK] 🟢 Botão salvar clicado!", {
      isLoading,
      timestamp: new Date().toISOString()
    });
    
    if (isLoading) {
      console.log("[BUTTON_CLICK] ❌ isLoading=true, ABORTANDO");
      return; // Previne cliques múltiplos
    }
    
    console.log("[BUTTON_CLICK] ✅ Chamando onSubmit...");
    await onSubmit();
    console.log("[BUTTON_CLICK] ✅ onSubmit concluído");
  };

  return (
    <MDBox>
      <Grid container spacing={3}>
        <Grid size={{ xs: 12, lg: 4 }}>
          <MDBox height="100%">
            <ProductImage messages={messages} />
          </MDBox>
        </Grid>

        <Grid size={{ xs: 12, lg: 8 }}>
          <MDBox height="100%">
            <ProductInfo messages={messages} isEditing={isEditing} />
          </MDBox>
        </Grid>

        <Grid size={{ xs: 12, lg: 4 }} sx={{ alignSelf: "flex-start" }}>
          <AdditionalInfo ref={additionalInfoCardRef} messages={messages} />
        </Grid>

        <Grid size={{ xs: 12, lg: 8 }} sx={{ alignSelf: "flex-start" }}>
          <MDBox sx={{ width: "100%" }}>
            <Categories
              messages={messages}
              productId={productId}
              cardHeightPx={categoriesCardHeightPx}
            />
          </MDBox>
        </Grid>

        <Grid size={12}>
          <MDBox height="100%">
            <VariationInfo messages={messages} />
          </MDBox>
        </Grid>
      </Grid>

      <MDBox mt={2} display="flex" justifyContent="flex-end" gap={2}>
        <MDButton
          variant="gradient"
          color="light"
          onClick={() => router(ROUTE_URLS.products(lang))}
          isLoading={isLoading}
        >
          {messages.cancelButton}
        </MDButton>

        <MDButton 
          variant="gradient" 
          color="dark" 
          onClick={handleSaveClick} 
          isLoading={isLoading}
          disabled={isLoading}
        >
          {messages.updateButton}
        </MDButton>
      </MDBox>
    </MDBox>
  );
}

export default ProductForm;
